import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';
import { rem } from 'polished';
import styled, { css } from 'styled-components';

import content from '../../content/header.json';
import { Container } from '../../static/styles/helpers';
import { Button } from '../button';
import { ContactModal } from '../contact-modal';

const HeaderWrapper = styled(motion.header)<{ scrolled: boolean }>`
  background-color: ${props => props.theme.colors.red};
  display: flex;
  height: ${rem(116)};
  left: 0;
  position: fixed;
  top: 0;
  transition: background 0.3s;
  width: 100%;
  z-index: 10;

  button {
    transition: background 0.3s;
  }

  ${props =>
    props.scrolled &&
    css`
      background: ${props => props.theme.colors.gray};

      ${props => props.theme.mediaQuery('lg', 'min')} {
        button {
          background: ${props => props.theme.colors.red};

          &:hover {
            background: ${props => props.theme.colors.redHover};
          }
        }
      }
    `}

  ${props => props.theme.mediaQuery('lg', 'max')} {
    height: ${rem(80)};
  }

  ${Container} {
    align-items: center;
    display: flex;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;

    ${props => props.theme.mediaQuery('lg', 'min')} {
      display: flex;
      height: 100%;
    }

    ${props => props.theme.mediaQuery('lg', 'max')} {
      align-self: center;
      padding-left: ${rem(20)};
      padding-right: ${rem(20)};
      width: 100%;
    }

    li {
      align-items: center;
      color: ${props => props.theme.colors.white};
      cursor: pointer;
      display: flex;
      font-family: ${props => props.theme.fonts.MrsEavesSmallCaps};
      font-size: ${rem(18)};
      height: 100%;
      letter-spacing: ${rem(3)};
      position: relative;
      text-transform: uppercase;

      &.dropdown-link {
        a::after {
          display: none;
        }

        &:after {
          background-color: ${props => props.theme.colors.white};
          content: '';
          left: 0;
          position: absolute;
          transition: transform 0.25s ease-in-out;
          width: 100%;

          ${props => props.theme.mediaQuery('lg', 'min')} {
            height: ${rem(5)};
            top: 0;
            transform: scaleY(0);
            transform-origin: top;
          }

          ${props => props.theme.mediaQuery('lg', 'max')} {
            height: ${rem(2)};
            top: 100%;
            transform: scaleX(0);
            transform-origin: left;
          }
        }

        ${props => props.theme.mediaQuery('lg', 'min')} {
          &:hover:after {
            transform: none;
          }
        }
      }

      ${props => props.theme.mediaQuery('lg', 'min')} {
        /* display: flex; */
        /* align-items: center; */
      }

      ${props => props.theme.mediaQuery('lg', 'max')} {
        font-size: ${rem(22)};
        position: relative;
      }

      ${props => props.theme.mediaQuery('lg', 'max')} {
        display: block;
      }

      &:not(:last-child) {
        ${props => props.theme.mediaQuery('lg', 'min')} {
          margin-right: ${rem(24)};
        }

        ${props => props.theme.mediaQuery('lg', 'max')} {
          margin-bottom: ${rem(15)};
        }
      }
    }

    a {
      &:after {
        background-color: ${props => props.theme.colors.white};
        content: '';
        left: 0;
        position: absolute;
        transition: transform 0.25s ease-in-out;
        width: 100%;

        ${props => props.theme.mediaQuery('lg', 'min')} {
          height: ${rem(5)};
          top: 0;
          transform: scaleY(0);
          transform-origin: top;
        }

        ${props => props.theme.mediaQuery('lg', 'max')} {
          height: ${rem(2)};
          top: 100%;
          transform: scaleX(0);
          transform-origin: left;
        }
      }

      &[aria-current='page'],
      &:hover {
        &:after {
          transform: none;
        }
      }
    }
  }

  a {
    color: ${props => props.theme.colors.white};
  }
`;

const LogoLink = styled(Link)`
  ${props => props.theme.mediaQuery('lg', 'max')} {
    position: relative;
    width: ${rem(150)};
    z-index: 2;
  }
`;

const Menu = styled.div<{ open: boolean }>`
  display: flex;
  height: 100%;
  margin: 0 0 0 auto;

  ${props => props.theme.mediaQuery('lg', 'max')} {
    height: 100%;
    left: -100%;
    position: fixed;
    top: 0;
    transition: 0s 0.5s;
    width: 100%;
    z-index: 1;

    li {
      opacity: 0;
      transform: translateX(${rem(-20)});
      transition: 0.3s;
    }

    &::before {
      background: ${props => props.theme.colors.red};
      content: '';
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      transform: scaleY(0);
      transform-origin: top;
      transition: 0.5s;
      width: 100%;
    }

    ${props =>
      props.open &&
      css`
        left: 0;
        transition: 0s 0s;

        li {
          opacity: 1;
          transform: translateX(0);

          &:nth-of-type(1) {
            transition: 0.5s 0.3s;
          }

          &:nth-of-type(2) {
            transition: 0.5s 0.4s;
          }

          &:nth-of-type(3) {
            transition: 0.5s 0.5s;
          }

          &:nth-of-type(4) {
            transition: 0.5s 0.6s;
          }

          &:nth-of-type(5) {
            transition: 0.5s 0.7s;
          }

          &:nth-of-type(6) {
            transition: 0.5s 0.8s;
          }
        }

        &::before {
          transform: scaleY(1);
        }
      `}
  }
`;

const Burger = styled.button<{ open: boolean }>`
  background: none;
  cursor: pointer;
  display: inline-block;
  height: ${rem(20)};
  margin-left: auto;
  position: relative;
  top: ${rem(-5)};
  transition: 0.3s;
  vertical-align: top;
  width: ${rem(36)};
  z-index: 3;

  ${props => props.theme.mediaQuery('lg', 'min')} {
    display: none;
  }

  span {
    background: ${props => props.theme.colors.white};
    height: 2px;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;

    &:nth-of-type(1) {
      transition:
        all 0.3s 0.3s,
        transform 0.3s 0s;
    }

    &:nth-of-type(2) {
      left: 0;
      margin-top: -1px;
      top: 50%;
      transition: all 0.3s 0.3s;
    }

    &:nth-of-type(3) {
      bottom: 0;
      left: 0;
      top: auto;
      transition:
        all 0.3s 0.3s,
        transform 0.3s 0s;
    }
  }

  ${props =>
    props.open &&
    css`
      span {
        &:nth-of-type(1) {
          margin-top: -1px;
          top: 50%;
          transform: rotate(45deg);
          transition:
            all 0.3s,
            transform 0.3s 0.3s;
        }

        &:nth-of-type(2) {
          opacity: 0;
          transition:
            all 0.3s,
            opacity 0.3s 0s;
        }

        &:nth-of-type(3) {
          bottom: 50%;
          margin-bottom: -1px;
          transform: rotate(-45deg);
          transition:
            all 0.3s,
            transform 0.3s 0.3s;
        }
      }
    `}
`;

const DropdownContent = styled.span`
  display: flex;
  padding-top: ${rem(10)};
  will-change: transform;

  ${props => props.theme.mediaQuery('lg', 'min')} {
    left: 50%;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    top: 100%;
    transform: translateX(-50%) translateY(${rem(-10)});
    transition:
      transform 0.3s,
      opacity 0.3s;
  }

  a {
    font-style: italic;
    opacity: 0.32;
    padding: 0 10px;
    position: relative;
    text-transform: none;
    transition: opacity 0.3s;
    white-space: nowrap;

    &:hover,
    &[aria-current='page'] {
      opacity: 1;
    }

    &:not(:last-of-type) {
      &::before {
        content: '│';
        margin-right: -10px;
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
      }
    }

    &::after {
      display: none;
    }
  }
`;

const Dropdown = styled.span`
  display: inline-block;
  position: relative;
  vertical-align: top;

  ${props => props.theme.mediaQuery('lg', 'min')} {
    &:hover {
      ${DropdownContent} {
        opacity: 1;
        pointer-events: auto;
        transform: translateX(-50%) translateY(${rem(-5)});
      }
    }
  }
`;

export default function Header() {
  const [modalIsOpen, setIsOpen] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  const checkPosition = () => {
    if (window.scrollY > 0) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
  };

  const onContactClick = () => {
    if (modalIsOpen) {
      closeModal();
    } else {
      openModal();
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', checkPosition);

    checkPosition();
  });

  return (
    <HeaderWrapper
      scrolled={scrolled}
      initial={{
        y: -20,
        opacity: 0,
      }}
      animate={{
        y: 0,
        opacity: 1,
        transition: { duration: 0.5, delay: 0, ease: [0.24, 0.1, 0.25, 1] },
      }}
    >
      <Container>
        <LogoLink
          to="/"
          onClick={() => {
            setMenuOpen(false);
          }}
        >
          <StaticImage
            src="../../static/images/logo-white.png"
            height={80}
            alt="OCAA logo"
            placeholder="none"
          />
        </LogoLink>

        <Burger
          open={menuOpen}
          aria-label="Toggle menu"
          onClick={() => {
            setMenuOpen(!menuOpen);
          }}
        >
          <span />
          <span />
          <span />
        </Burger>

        <Menu open={menuOpen}>
          <ul>
            {content.links.map((link, index) => {
              const isLast = index === content.links.length - 1;
              const hasDropdown = typeof link.dropdown != 'undefined';

              return (
                <li
                  className={hasDropdown ? 'dropdown-link' : ''}
                  key={`${link.slug}.${index}`}
                >
                  {hasDropdown ? (
                    <Dropdown>
                      <Link to={link.dropdown[0].slug}>{link.title}</Link>
                      <DropdownContent>
                        {link.dropdown.map(item => (
                          <Link
                            to={item.slug}
                            key={item.title}
                            onClick={() => {
                              setMenuOpen(false);
                            }}
                          >
                            {item.title}
                          </Link>
                        ))}
                      </DropdownContent>
                    </Dropdown>
                  ) : (
                    <Link
                      to={link.slug}
                      key={link.slug}
                      onClick={e => {
                        setMenuOpen(false);

                        if (isLast) {
                          e.preventDefault();
                          onContactClick();
                        }
                      }}
                    >
                      {isLast ? <Button title={link.title} /> : link.title}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </Menu>
      </Container>
      <ContactModal isOpen={modalIsOpen} closeModal={closeModal} />;
    </HeaderWrapper>
  );
}
